/**
 * Grayscale coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const grayscaleColoringPagesPack: ToilePack = {
    slug: 'grayscale-coloring-pages',
    category: 'coloring',
    name: 'Grayscale coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lion', label: 'Lion', subject: "a lion's head facing forward, a full mane in layered locks, whiskers and amber eyes in soft gray shading"},
        {key: 'lighthouse', label: 'Lighthouse', subject: 'a striped lighthouse on a rocky headland, waves breaking below and gulls over a cloudy sky'},
        {key: 'peonies', label: 'Peonies', subject: 'a glass vase of full-blown peonies with loose petals on a lace tablecloth'},
    ],
    styles: [
        {key: 'soft', label: 'Soft blended', style: 'smooth blended pencil-like gradients from light to dark, soft contour line', palette: 'soft gray tones from white to near black', framing: 'The subject centered with a white margin'},
        {key: 'etched', label: 'Etched ink', style: 'engraving-style hatching and stippling that builds the gray tones, crisp contour line', palette: 'gray tones built from ink hatching and dots', framing: 'The subject centered with a white margin'},
        {key: 'dramatic', label: 'High contrast', style: 'deep dark shadows against bright highlights, strong tonal contrast, a clear contour line', palette: 'gray tones from bright white to deep charcoal', framing: 'The subject centered with a white margin'},
    ],
    directions: ['wolf', 'horse', 'old barn'],
    directionHint: 'try: wolf',
    seo: {
        title: 'Grayscale coloring pages: lion, lighthouse, peonies',
        metaDescription: "Nine grayscale coloring pages: a lion's head, a lighthouse and peonies in soft gray tones, from light to dark.",
        h1: "Grayscale coloring pages: lion's head, lighthouse, peonies",
        tagline: 'Gray shading to color over with pencils, from light tones to deep shadow.',
        intro: "Grayscale coloring pages for experienced adult colorers who color over printed gray shading to get depth with pencils. The nine pages show a lion's head, a lighthouse on a rocky shore and a vase of peonies, in gray tones from light to dark. The styles are soft blended, etched ink and high contrast. Type a different subject, such as a wolf or an old barn, and the same tonal style carries it.",
        useCases: [
            'a realistic study page for pencil colorists',
            'a tonal exercise before adding color',
            'a framed gray study for a home or studio wall',
            'a practice page for shading with pencils',
        ],
        faq: [
            {q: 'How are the pages different from line coloring pages?', a: 'These are not line drawings to fill: each page is printed with gray tones that you color over to build depth.'},
            {q: 'What is the difference between the styles?', a: 'Soft blended uses smooth gradients from light to dark. Etched ink uses engraving-style hatching and stippling, and high contrast uses deep shadows against bright highlights.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. A subject you type, such as a wolf, a horse or an old barn, is a request; the page keeps the same tonal style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. The pages are in gray tones only, from white to near black, for you to add color over them.'},
        ],
    },
}
